import api from './api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ReviewEntityType = 'wine' | 'producer' | 'region' | 'grape' | 'wine_name';

export const REVIEW_TYPE_LABELS: Record<ReviewEntityType, string> = {
  wine:      'Wine',
  producer:  'Producer',
  region:    'Region',
  grape:     'Grape',
  wine_name: 'Wine Name',
};

// Types that support reject (soft-delete) on the backend.
// As of U1H all five identity types have an `active` field, so reject is
// supported across the board.
export const REJECT_SUPPORTED: Set<ReviewEntityType> = new Set<ReviewEntityType>([
  'wine', 'wine_name', 'producer', 'region', 'grape',
]);

export type ReviewItemSource = 'admin' | 'venue_request' | 'import';

export interface ReviewItemCreatedBy {
  _id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface ReviewQueueItem {
  id: string;
  type: ReviewEntityType;
  displayName: string;
  keyFields: Record<string, unknown>;
  active: boolean | null;
  /** Provenance — populated for records created via venue request or import. */
  source?: ReviewItemSource;
  createdBy?: ReviewItemCreatedBy | null;
  createdAt: string;
  /**
   * SIMPLE-WINE.1 — identity fields this row is still missing (`producer`,
   * `region`). Empty or absent means it can be approved. Present so the
   * reason can be stated ON the approve control instead of only after a
   * refused click; the server enforces the gate regardless.
   */
  missingIdentity?: string[];
  /**
   * SIMPLE-WINE.2 §4 — which wine name a pending WINE hangs on. A reviewer
   * looking at a vintage cannot otherwise tell whether the record behind it
   * is trustworthy.
   */
  wineNameRef?: {
    id: string;
    displayName: string;
    provisional: boolean;
    missingIdentity: string[];
  } | null;
  /**
   * SIMPLE-WINE.2 §2 — a provisional wine name carries its pending vintages
   * here, and those vintages do not appear separately in the queue. One
   * decision, one card.
   */
  pendingWines?: ReviewQueueItem[];
  /** Present on the response to an approve that also took the vintages. */
  approvedWines?: string[];
  failedWines?: Array<{ _id: string; reason: string }>;
}

/** EVIDENCE.1a — one item of what a record was made from. */
export interface EvidenceItem {
  _id: string;
  kind: 'typed_text' | 'photo' | 'scan_parse' | 'reviewer_note';
  text: string | null;
  url: string | null;
  /** The object was purged by retention; the row survives as a tombstone. */
  purged: boolean;
  sizeBytes: number | null;
  venueId: string;
  actorEmail: string | null;
  createdAt: string | null;
}

export interface CandidateVintage {
  _id: string;
  displayVintage: string;
}

/**
 * SIMPLE-WINE.3 — a canonical wine name a provisional could be matched onto.
 * Decorated with producer, region and existing vintages, because "is this the
 * same wine?" is not answerable from a display name alone.
 */
export interface PossibleMatch {
  _id: string;
  displayName: string;
  color?: string;
  producerName?: string | null;
  regionName?: string | null;
  vintages: CandidateVintage[];
  /** Only on ranked suggestions; absent from a plain search result. */
  sharedTokens?: number;
  overlap?: number;
}

export interface ResolvePlanWine {
  wineId: string;
  name: string;
  displayVintage: string;
  vintageType: string;
  vintage?: number;
  action: 'move' | 'attach';
  existingWineId?: string;
  refs: {
    inventoryItems: number; wineListItems: number; menuItems: number; products: number;
  };
}

export interface ResolvePlan {
  ok: boolean;
  provisional?: { id: string; displayName: string };
  target?: { id: string; displayName: string };
  wines: ResolvePlanWine[];
  aliasToAdd: string | null;
  summary: string[];
}

export interface ResolveResult {
  resolved: boolean;
  moved: string[];
  attached: Array<{ from: string; to: string }>;
  aliasAdded: string | null;
  approvable: string[];
}

export interface ReviewQueueSummary {
  total: number;
  wine: number;
  producer: number;
  region: number;
  grape: number;
  wine_name: number;
}

export interface ReviewQueueResponse {
  summary: ReviewQueueSummary;
  items: ReviewQueueItem[];
  /**
   * `records` counts rows waiting for review; `cards` counts the units shown,
   * and a provisional pair is one unit. They legitimately differ.
   */
  pagination: {
    page: number; limit: number; total: number; cards?: number; records?: number;
  };
}

// ── Edit URL helper ───────────────────────────────────────────────────────────

export function getEditUrl(type: ReviewEntityType, id: string): string {
  switch (type) {
    case 'wine':      return `/admin/wines/edit/${id}`;
    case 'producer':  return `/admin/producers/edit/${id}`;
    case 'region':    return `/admin/regions/${id}/edit`;
    case 'grape':     return `/admin/grapes/edit/${id}`;
    case 'wine_name': return `/admin/wine-names/edit/${id}`;
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

const BASE = '/admin/governance/review-queue';

const reviewQueueService = {
  getQueue: async (
    type?: ReviewEntityType,
    page = 1,
    limit = 50,
  ): Promise<ReviewQueueResponse> => {
    const params: Record<string, string | number> = { page, limit };
    if (type) params.type = type;
    const res = await api.get<ReviewQueueResponse>(BASE, { params });
    return res.data;
  },

  /**
   * `withPendingWines` is opt-in: approving a wine name can approve the
   * vintages hanging off it, but only when the reviewer asked from a card
   * that showed them which wines those are.
   */
  approve: async (
    type: ReviewEntityType,
    id: string,
    opts?: { withPendingWines?: boolean },
  ): Promise<ReviewQueueItem> => {
    const q = opts?.withPendingWines ? '?withPendingWines=true' : '';
    const res = await api.patch<ReviewQueueItem>(`${BASE}/${type}/${id}/approve${q}`);
    return res.data;
  },

  /**
   * EVIDENCE.1a — what this record was made from. Failing quietly is right:
   * evidence is context, and losing it must not cost the reviewer the screen.
   */
  evidenceItems: async (wineNameId: string): Promise<EvidenceItem[]> => {
    const res = await api.get<{ items: EvidenceItem[] }>(
      `/admin/wine-name/${wineNameId}/evidence-items`,
    );
    return res.data?.items ?? [];
  },

  /** SIMPLE-WINE.2 §4 — canonical names that resemble this provisional one. */
  possibleMatches: async (wineNameId: string): Promise<PossibleMatch[]> => {
    const res = await api.get<{ data: PossibleMatch[] }>(
      `/admin/wine-name/${wineNameId}/possible-matches`,
    );
    return res.data?.data ?? [];
  },

  /** SIMPLE-WINE.3 — search ALL canonical names, when no suggestion fits. */
  candidates: async (wineNameId: string, q: string): Promise<PossibleMatch[]> => {
    const res = await api.get<{ data: PossibleMatch[] }>(
      `/admin/wine-name/${wineNameId}/candidates`, { params: q ? { q } : {} },
    );
    return res.data?.data ?? [];
  },

  /**
   * Exactly what confirming would do. Built by the same code that performs
   * the action, so what a reviewer approves is what happens.
   */
  resolvePreview: async (wineNameId: string, targetWineNameId: string): Promise<ResolvePlan> => {
    const res = await api.get<ResolvePlan>(
      `/admin/wine-name/${wineNameId}/resolve-preview`,
      { params: { targetWineNameId } },
    );
    return res.data;
  },

  /**
   * Match this provisional onto a canonical wine name.
   *
   * `suggestionsShown` is what the reviewer was actually looking at. The
   * server cannot know it — the list was rendered here — and without it every
   * decision logs a null rank, which would make the recall signal (§9)
   * indistinguishable from "we never asked".
   */
  resolve: async (
    wineNameId: string,
    targetWineNameId: string,
    suggestionsShown: Array<{ wineNameId: string; overlap: number; rank: number }> = [],
  ): Promise<ResolveResult> => {
    const res = await api.post(`/admin/wine-name/${wineNameId}/resolve`, {
      targetWineNameId, suggestionsShown,
    });
    return res.data;
  },

  reject: async (type: ReviewEntityType, id: string): Promise<ReviewQueueItem> => {
    const res = await api.patch<ReviewQueueItem>(`${BASE}/${type}/${id}/reject`);
    return res.data;
  },
};

export default reviewQueueService;
